import React from "react";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Paper,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import FilaMovimiento from "./FilaMovimiento.js";

const useStyles = makeStyles((theme) => ({
  expandSection: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  expandHeader: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  expandContent: {
    padding: theme.spacing(2),
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

/**
 * Props del componente
 * @param {String} sectionKey - Clave de la sección ('perfectos', 'buenos', etc.)
 * @param {String} titulo - Título de la sección
 * @param {ReactElement} icono - Ícono a mostrar
 * @param {Number} cantidad - Cantidad total de movimientos
 * @param {Array} movimientos - Array de movimientos a mostrar
 * @param {String} bgColor - Color de fondo del header
 * @param {Boolean} expanded - Si está expandida o no
 * @param {Function} onToggle - Handler para expandir/colapsar
 * @param {Object} pagination - Objeto con page y rowsPerPage
 * @param {Function} onChangePage - Handler para cambiar página
 * @param {Function} onChangeRowsPerPage - Handler para cambiar filas por página
 * @param {Boolean} showActions - Si muestra botones de acción
 * @param {String} categoria - Categoría para estilizar filas
 * @param {Function} onVerSugerencias - Handler para ver sugerencias
 * @param {Function} onConciliarManual - Handler para conciliar manual
 * @param {Function} onConciliarIndividual - Handler para conciliar individual
 * @param {Boolean} procesandoAccion - Si hay una acción en curso
 * @param {Set<number>} movimientosEnProceso - Set de IDs de movimientos en proceso
 */
export default function TablaMovimientos({
  sectionKey,
  titulo,
  icono,
  cantidad,
  movimientos,
  bgColor,
  expanded,
  onToggle,
  pagination,
  onChangePage,
  onChangeRowsPerPage,
  showActions,
  categoria,
  onVerSugerencias,
  onConciliarManual,
  onConciliarIndividual,
  procesandoAccion,
  movimientosEnProceso,
}) {
  const classes = useStyles();

  // Si no hay movimientos, no renderizar nada
  if (cantidad === 0) return null;

  const { page, rowsPerPage } = pagination;

  // Calcular movimientos a mostrar en la página actual
  const movimientosPaginados = movimientos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box className={classes.expandSection}>
      {/* ==================== HEADER EXPANDIBLE ==================== */}
      <Box
        className={classes.expandHeader}
        onClick={onToggle}
        style={{ backgroundColor: bgColor }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {icono}
          <Typography variant="h6">{titulo}</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: 8 }}
          >
            ({cantidad} {cantidad === 1 ? "movimiento" : "movimientos"})
          </Typography>
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      {/* ==================== CONTENIDO COLAPSABLE ==================== */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className={classes.expandContent}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              {/* ==================== HEADER DE TABLA ==================== */}
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Fecha Mov.</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Banco</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>
                    RUT Ordenante
                  </TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Glosa</TableCell>
                  <TableCell align="right" style={{ fontWeight: 600 }}>
                    Monto
                  </TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Score</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Facturas</TableCell>
                  {showActions && (
                    <TableCell align="center" style={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              {/* ==================== BODY DE TABLA ==================== */}
              <TableBody>
                {movimientosPaginados.map((detalle, index) => (
                  <FilaMovimiento
                    key={detalle.movimiento_id || index}
                    detalle={detalle}
                    showActions={showActions}
                    categoria={categoria}
                    onVerSugerencias={onVerSugerencias}
                    onConciliarManual={onConciliarManual}
                    onConciliarIndividual={onConciliarIndividual}
                    procesandoAccion={procesandoAccion}
                    isProcessing={
                      !!movimientosEnProceso?.[String(detalle.movimiento_id)]
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ==================== PAGINACIÓN ==================== */}
          <TablePagination
            component="div"
            count={movimientos.length}
            page={page}
            onPageChange={(e, newPage) => onChangePage(sectionKey, newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onChangeRowsPerPage(sectionKey, e)}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
